/* eslint-disable id-length */

import { Node, Position, getIncomers, getOutgoers } from "@xyflow/react";
import { NodeType } from "~/lib/pipeline/graph/NodeType";
import { transitiveReduction } from "~/lib/pipeline/graph/transitiveReduction";
import { Graph } from "~/lib/pipeline/graph/Graph";
import { Step, State, DependencyType, Type, BlockStep } from "~/lib/pipeline/Step";
import { EdgeType } from "~/lib/pipeline/graph/EdgeType";
import { Edge, createEdge, EdgeWithConnections } from "~/lib/pipeline/graph/Edge";
import { remapCollapsedGroupEdges } from "~/lib/pipeline/graph/remapCollapsedGroupEdges";
import { AdjacencyMap } from "~/lib/pipeline/graph/AdjacencyMap";

function isBlockStepNode(step: Step): step is BlockStep {
  return (
    step.type === Type.Block ||
    (step.type === Type.Input &&
      step.dependencies.filter((dep) => dep.type === DependencyType.Gate).length > 0)
  );
}

function getNodeTypeForStep(step: Step): NodeType {
  switch (step.type) {
    case "input":
      if (isBlockStepNode(step)) {
        return NodeType.Block;
      }
      return NodeType.Input;
    case "block":
      return NodeType.Block;
    case "wait":
      return NodeType.Wait;
    case "group":
      return NodeType.Group;
    case "command":
      return NodeType.Command;
    case "trigger":
      return NodeType.Trigger;
    default:
      console.error("Unknown step type: %o", step.type);
      return NodeType.Unknown;
  }
}

const ROOT_NODE_ID = "root";

function getRootNode(nodes: Map<string, Node>) {
  const root = nodes.get(ROOT_NODE_ID);
  if (!root) {
    throw new Error(`root node not found`);
  }
  return root;
}

function createNodesForSteps(steps: Step[], expandedGroupSteps?: Set<string>): Map<string, Node> {
  const root: Node = {
    id: ROOT_NODE_ID,
    type: NodeType.Root,
    position: { x: 0, y: 0 },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    data: {},
  };

  const nodes = steps.reduce(
    (acc, step) => {
      const node: Node = {
        id: step.uuid,
        type: getNodeTypeForStep(step),
        position: { x: 0, y: 0 },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        data: step,
      };

      if (step.type === Type.Group) {
        node.expandParent = expandedGroupSteps?.has(step.uuid);
      }

      if (step.groupUuid) {
        node.parentId = step.groupUuid;
        node.extent = "parent";
      }

      acc.set(node.id, node);
      return acc;
    },
    new Map<string, Node>([[root.id, root]]),
  );

  return nodes;
}

function createEdgesForSteps(
  steps: Step[],
  nodes: Map<string, Node>,
): Map<string, EdgeWithConnections> {
  const edges = new Map<string, EdgeWithConnections>();

  // Create edges for all the dependency-less steps, connecting them to the root node.
  steps
    .filter((step) => step.dependencies.length === 0)
    .forEach((step) => {
      const root = getRootNode(nodes);

      const target = nodes.get(step.uuid);
      if (!target) {
        throw new Error(`target node not found: ${step.uuid}`);
      }

      // If the node is a child a group, don't create an edge to the root.
      if (target && target.parentId) {
        return;
      }

      const edge = createEdge(root, target, EdgeType.ImplicitDependency);
      edges.set(edge.id, edge);
    });

  // Create edges for all the steps with dependencies.
  steps.flatMap((step) =>
    step.dependencies
      .map((dep) => {
        // If the dependency has yet to be resolved, connect it to the root node.
        if (!dep.uuid) {
          const source = getRootNode(nodes);

          const target = nodes.get(step.uuid);
          if (!target) {
            throw new Error(`target node not found: ${step.uuid}`);
          }

          return createEdge(source, target, EdgeType.ExplicitDependency);
        }

        const source = nodes.get(dep.uuid);
        if (!source) {
          throw new Error(`source node not found: ${dep.uuid}`);
        }

        const target = nodes.get(step.uuid);
        if (!target) {
          throw new Error(`target node not found: ${step.uuid}`);
        }

        return createEdge(
          source,
          target,
          dep.type === DependencyType.Direct
            ? EdgeType.ExplicitDependency
            : EdgeType.ImplicitDependency,
        );
      })
      .forEach((edge) => edges.set(edge.id, edge)),
  );

  return edges;
}

/**
 * Find the closest prior wait or block for a given node.
 */
function findClosestGate(node: Node, nodes: Node[]) {
  return nodes
    .slice(0, nodes.indexOf(node))
    .filter((n) => {
      if (node.parentId) {
        return (
          (n.type === NodeType.Wait || n.type === NodeType.Block) &&
          n.data.state !== State.Ignored &&
          n.parentId === node.parentId
        );
      }
      return (
        (n.type === NodeType.Wait || n.type === NodeType.Block) && n.data.state !== State.Ignored
      );
    })
    .pop();
}

/**
 * Get the step that uploaded this step.
 */
const getPipelineUpload = (step: Step, nodes: Map<string, Node>) => {
  return nodes.get(step.uploadedByStepUuid || "");
};

/**
 * Remove ignored steps and re-route outgoing steps.
 *
 * - If a step explicitly depends on an ignored step, it's re-routed to the relevant pipeline upload or root node.
 * - If a step implicitly depends on an ignored step, it's re-routed to either to the nearest wait, block, pipeline upload step, or root node.
 * - If a group sub-step explicitly depends on an ignored step, it's incoming edges are removed.
 * - If a group sub-step implicitly depends on an ignored step, it's re-routed to the nearest wait, block, pipeline upload step, or root node.
 * - If a group is now empty after removing ignored steps, it's removed.
 * - If a wait step is part of a chain of wait steps, only the first one is kept.
 * - If a wait step (outside of a group) doesn't have any downstream steps, it's removed.
 */
function removeIgnoredSteps(
  adjacencyMap: AdjacencyMap,
  nodes: Map<string, Node>,
  edges: Map<string, EdgeWithConnections>,
) {
  // Function to remove a node and re-route the edges.
  const removeNode = (node: Node) => {
    const nodesArray = Array.from(nodes.values());
    const edgesArray = Array.from(edges.values());

    const incomers = getIncomers(node, nodesArray, edgesArray);
    const outgoers = getOutgoers(node, nodesArray, edgesArray);

    const gate = findClosestGate(node, nodesArray);
    const root = getPipelineUpload(node.data as Step, nodes) || getRootNode(nodes);

    // Loop over all the outgoing nodes, essentially the ones dependant on the ignored node.
    outgoers.forEach((outgoer) => {
      const outgoingEdge = edges.get(`${node.id}-${outgoer.id}`) as Edge;
      edges.delete(outgoingEdge.id);
      adjacencyMap.delete(node.id, outgoer.id);

      // If the node is a sub-step of a group and there's no prior gate, don't create a new edge.
      if (outgoer.parentId && !gate) {
        return;
      }

      // If the outgoing edge is an _implicit_ dependency, re-route it to the nearest wait or block (if there is one).
      if (outgoingEdge.type === EdgeType.ImplicitDependency && gate) {
        if (!adjacencyMap.reachable(outgoer.id, gate.id)) {
          const edge = createEdge(gate, outgoer, EdgeType.ImplicitDependency);
          edges.set(edge.id, edge);
          adjacencyMap.add(gate.id, outgoer.id);
          return;
        }
      }

      // Re-route edge to either the step that uploaded it or the root node.
      const edge = createEdge(root, outgoer, outgoingEdge.type as EdgeType);
      edges.set(edge.id, edge);
      adjacencyMap.add(root.id, outgoer.id);
    });

    // Remove all incoming edges to the ignored node.
    incomers.forEach((incomer) => {
      edges.delete(`${incomer.id}-${node.id}`);
      adjacencyMap.delete(incomer.id, node.id);
    });

    nodes.delete(node.id);
  };

  // Remove all the ignored steps
  Array.from(nodes.values())
    .filter((node: Node) => node.data.state === State.Ignored)
    .forEach(removeNode);

  // Remove all the groups that are now empty after removing ignored steps.
  Array.from(nodes.values())
    .filter(
      (node) =>
        node.type === NodeType.Group &&
        Array.from(nodes.values()).filter((n) => n.parentId === node.id).length === 0,
    )
    .forEach(removeNode);

  // Remove wait steps that are part of a chain of wait steps, keeping only the first one.
  Array.from(edges.values())
    .filter(
      (edge) => edge.sourceNode?.type === NodeType.Wait && edge.targetNode?.type === NodeType.Wait,
    )
    .forEach((edge) => {
      if (!edge?.targetNode) {
        return;
      }

      const nodesArray = Array.from(nodes.values());
      const edgesArray = Array.from(edges.values());

      // Only remove the wait node if it has a single incoming edge.
      const incomers = getIncomers(edge?.targetNode, nodesArray, edgesArray);
      if (incomers.length === 1) {
        removeNode(edge?.targetNode);
      }
    });

  // Remove orphaned wait steps (outside of a group) that don't have any downstream steps.
  Array.from(nodes.values())
    .filter((node) => node.type === NodeType.Wait && !node.parentId)
    .forEach((node) => {
      const nodesArray = Array.from(nodes.values());
      const edgesArray = Array.from(edges.values());

      const outgoers = getOutgoers(node, nodesArray, edgesArray);
      if (outgoers.length > 0) {
        return;
      }

      const incomers = getIncomers(node, nodesArray, edgesArray);
      incomers.forEach((incomer) => {
        edges.delete(`${incomer.id}-${node.id}`);
      });

      nodes.delete(node.id);
    });
}

/**
 * Remap group children that are dependencies of top-level wait or block steps.
 *
 * Ideally we should only have implicit dependencies between steps at the same level as each other, but currently we have
 * implicit dependencies between group children and top-level wait or block steps. This function removes those implicit dependencies
 * and adds a **new** implicit dependency between the group and wait (or block) step.
 */
function flattenImplicitGroupChildrenDependencies(
  adjacencyMap: AdjacencyMap,
  nodes: Map<string, Node>,
  edges: Map<string, Edge>,
) {
  Array.from(edges.values())
    .filter(
      (edge) =>
        edge.type === EdgeType.ImplicitDependency &&
        edge.data?.sourceParentId !== edge.data?.targetParentId,
    )
    .forEach((edge) => {
      const target = nodes.get(edge.target);
      if (!target) {
        return;
      }

      // If the target isn't a wait or block step, don't do anything.
      if (![NodeType.Wait, NodeType.Block].includes(target.type as NodeType)) {
        return;
      }

      const source = nodes.get(edge.source);
      const group = source?.parentId ? nodes.get(source?.parentId) : null;
      if (!group) {
        return;
      }

      const groupEdge = createEdge(group, target, EdgeType.ImplicitDependency);
      edges.set(groupEdge.id, groupEdge);
      adjacencyMap.add(groupEdge.source, groupEdge.target);

      edges.delete(edge.id);
      adjacencyMap.delete(edge.source, edge.target);
    });
}

interface Options {
  removeIgnoredSteps?: boolean;
  flattenImplicitGroupChildrenDependencies?: boolean;
  expandedGroupSteps?: Set<string>;
}

export function createGraphFromSteps(steps: Step[], options?: Options): Graph {
  const nodes = createNodesForSteps(steps, options?.expandedGroupSteps);
  const edges = createEdgesForSteps(steps, nodes);

  // Create an adjacency map representing the dependency graph that we later use for graph
  // traversal and avoiding the creation of cycles in subsequent transformations.
  const adjacencyMap = new AdjacencyMap(edges);

  if (options?.removeIgnoredSteps) {
    removeIgnoredSteps(adjacencyMap, nodes, edges);
  }

  if (options?.flattenImplicitGroupChildrenDependencies) {
    flattenImplicitGroupChildrenDependencies(adjacencyMap, nodes, edges);
  }

  remapCollapsedGroupEdges(adjacencyMap, nodes, edges);

  const graph = {
    nodes: Array.from(nodes.values()),
    edges: transitiveReduction(Array.from(edges.values())),
  };

  return graph;
}
